<template>
  <vue-scroll
    class="view box grow flex grid-2"
    v-loading.fullscreen.lock="fullscreenloading || trailSummaryStatus === 'inprogress'"
  >
    <div class="box grow login-container">
      <div class="grid-logo">
        <div class="logo-box align-vertical">
          <div class="logo-container align-vertical-middle">
            <div>
              <img class="logo-letter" src="/static/images/horizontal_logo_white.svg" alt="logo" />
              <div class="logo-desc">Autonomous Detection and Response</div>
            </div>
          </div>
        </div>
      </div>
      <el-form
        class="flex align-center grid-content login-page align-vertical"
        :model="userForm"
        :rules="userRules"
        ref="loginRef"
      >
        <div class="box form-wrapper align-vertical-middle">
          <div class="form-box">
            <div class="small-font h3"><h3>Sign in to your account</h3></div>
            <float-label class="styled">
              <el-form-item prop="email">
                <el-input type="email" placeholder="E-mail" v-model.trim="userForm.email" />
              </el-form-item>
            </float-label>
            <float-label class="styled">
              <input
                type="password"
                placeholder="Password"
                v-model="userForm.password"
                v-on:keyup.enter="login(userForm.email, userForm.password)"
              />
            </float-label>
            <div v-if="authError" class="error">
              <label>{{ authError }}</label>
            </div>

            <div class="flex text-center center mt-8 mb-8">
              <el-button plain @click="login(login.email, login.password)" class="login-btn">Sign In</el-button>
            </div>

            <div class="flex">
              <div class="box grow body1">
                <span class="float-right">
                  <router-link to="/forgot-password">Forgot Password?</router-link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </el-form>
    </div>
  </vue-scroll>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { AUTH_LOGIN, AUTH_TOKEN_REFRESH_INTERVAL, SSO_LOGIN } from '@/store/modules/auth/auth.type'
import { FETCH_NAV, UPDATE_NAV, NAV_SUCCESS } from '@/store/modules/navigation/nav.type'
import { TRAIL_SUMMARY_REQ } from '@/store/modules/trails/trailsinfo.type'
import { USER_PROFILE, USER_PERMISSION_SET } from '@/store/modules/user/user.type'
import { INSIGHTS_ENABLED } from '@/store/modules/feature_enabled/feature_enabled.type'
import { IQHUB_VERSION_REQ, IQ_PORTAL_VERSION_REQ } from '@/store/modules/version/version.type'
import { emailRe } from '@/utilities/regex/email'
import { OKTA_ORGANIZATION_URL, AZURE_ORGANIZATION_URL } from '@/constants/sso.constants'

export default {
  name: 'Login',
  data () {
    return {
      userForm: {
        email: '',
        password: ''
      },
      userRules: {
        email: [
          {
            required: true,
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (value === '') {
                callback(new Error("Please input the user's email address"))
              } else {
                emailRe.test(value) ? callback() : callback(new Error('Invalid email format'))
              }
            }
          }
        ]
      },
      fullscreenloading: false,
      isOrgOkta: true
    }
  },
  methods: {
    ...mapMutations({
      resetPrevRoute: 'PREV_ROUTE'
    }),
    ssoLogin (userData) {
      this.$store
        .dispatch(SSO_LOGIN, {
          firstName: userData['firstName'],
          lastName: userData['lastName'],
          email: userData['email'],
          accessToken: userData['accessToken']
        })
        .then(
          jwt => {
            // GET user profile after login (roles, permissions)
            // GET system summary - directs to different dashboard view depending on attack trails detected
            Promise.all([
              this.$store.dispatch(USER_PROFILE, {
                email: userData.email
              }),
              this.$store.dispatch(TRAIL_SUMMARY_REQ),
              this.$store.dispatch(FETCH_NAV),
              this.$store.dispatch(INSIGHTS_ENABLED),
              this.$store.dispatch(IQHUB_VERSION_REQ)
            ]).then(
              values => {
                this.fullscreenloading = false
                let profile = values[0]
                let summaryInfo = values[1]

                let permissions = _.flatten(_.map(profile.roles, 'permissions'))
                this.$store.dispatch(USER_PERMISSION_SET, permissions)
                this.$store.dispatch(UPDATE_NAV, permissions)
                this.$store.dispatch(NAV_SUCCESS)

                // set interval to refresh access tokens
                this.$store.dispatch(AUTH_TOKEN_REFRESH_INTERVAL)

                // direct user to landing page
                // main system health dashboard
                if (this.insightsEnabled) {
                  this.$router.push('/home')
                } else if (summaryInfo.numActiveTrails > 0) {
                  // land on attack trail dashboard if any exists
                  this.$router.push({ name: 'dashboard-trails' })
                } else if (summaryInfo.numPrivilegedTrails > 0) {
                  // else direct to privilege trail dashboard
                  this.$router.push({ name: 'dashboard-privileged' })
                } else {
                  this.$router.push({ name: 'dashboard-agents' })
                }
              },
              errMsg => {
                if (this.insightsEnabled) {
                  this.$router.push('/home')
                } else {
                  this.$router.push({ name: 'dashboard-trails' })
                }
              }
            )
          },
          errMsg => {
            if (errMsg.hasOwnProperty('isActive')) {
              if (!errMsg.isActive) {
                alert("User is deactivated by admin, can't access the application.")
                window.location.href = this.isOrgOkta ? OKTA_ORGANIZATION_URL : AZURE_ORGANIZATION_URL;
              }
            }
          }
        )
    },
    login () {
      this.$refs['loginRef'].validate(valid => {
        if (valid) {
          this.$store
            .dispatch(AUTH_LOGIN, {
              email: this.userForm.email,
              password: this.userForm.password
            })
            .then(
              jwt => {
                // GET user profile after login (roles, permissions)
                // GET system summary - directs to different dashboard view depending on attack trails detected
                Promise.all([
                  this.$store.dispatch(USER_PROFILE, {
                    email: this.userForm.email
                  }),
                  this.$store.dispatch(TRAIL_SUMMARY_REQ),
                  this.$store.dispatch(FETCH_NAV),
                  this.$store.dispatch(INSIGHTS_ENABLED),
                  this.$store.dispatch(IQHUB_VERSION_REQ)
                ]).then(
                  values => {
                    let profile = values[0]
                    let summaryInfo = values[1]

                    let permissions = _.flatten(_.map(profile.roles, 'permissions'))
                    this.$store.dispatch(USER_PERMISSION_SET, permissions)
                    this.$store.dispatch(UPDATE_NAV, permissions)
                    this.$store.dispatch(NAV_SUCCESS)

                    // set interval to refresh access tokens
                    this.$store.dispatch(AUTH_TOKEN_REFRESH_INTERVAL)

                    // direct user to landing page
                    // main system health dashboard
                    if (this.insightsEnabled) {
                      this.$router.push('/home')
                    } else if (summaryInfo.numActiveTrails > 0) {
                      // land on attack trail dashboard if any exists
                      this.$router.push({ name: 'dashboard-trails' })
                    } else if (summaryInfo.numPrivilegedTrails > 0) {
                      // else direct to privilege trail dashboard
                      this.$router.push({ name: 'dashboard-privileged' })
                    } else {
                      this.$router.push({ name: 'dashboard-agents' })
                    }

                    // direct to attack-details / accounts page
                    // if any previous route present
                    // (for users coming from email templates)
                    if (!_.isEmpty(this.prevRoute)) {
                      let prevPath = JSON.parse(JSON.stringify(this.prevRoute))
                      this.resetPrevRoute({})
                      this.$router.push({ path: prevPath.fullPath })
                    }
                  },
                  errMsg => {
                    if (this.insightsEnabled) {
                      this.$router.push('/home')
                    } else {
                      this.$router.push({ name: 'dashboard-trails' })
                    }
                  }
                )
              },
              errMsg => {
                console.log(errMsg)
              }
            )
        }
      })
    }
  },
  computed: {
    ...mapGetters(['authError', 'trailSummaryStatus', 'prevRoute', 'insightsEnabled', 'iqportalVersion'])
  },
  created () {
    this.$store.dispatch(IQ_PORTAL_VERSION_REQ)

    if (!_.isEmpty(this.$route.query)) {
      if(_.has(this.$route.query), 'org') {
        this.isOrgOkta = this.$route.query.org === "okta";
      }
      this.fullscreenloading = true
      this.ssoLogin(this.$route.query)
    }
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/global_classes.css';
@import 'assets/styles/text.css';
@import 'assets/styles/login.css';

.layout-container .container .view {
  padding: 0px;
  padding-bottom: 0px;
}

.error {
  text-align: center;
  color: var(--text-color-danger);
}

.company-desc {
  font-size: 0.8em;
  color: black;
  margin-top: 24px;
}

.desc {
  font-size: 0.8em;
  color: black;
  margin-top: 68px;
}

>>> .el-loading-mask {
  background-color: white;
}

.logo-container {
  margin-right: 80px; /* account for text bleed over passed svg */

  > div {
    height: 328px; /* Match height of right grid content */
  }
}

.el-form-item.is-error {
    margin-bottom: 35px;
}
</style>



// WEBPACK FOOTER //
// src/views/login/Login.vue